import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { V2AdminListOutreachReplyClassificationResponses } from '@workspace/api-backend'
import {
  v2AdminGetOutreachReplyStatsOptions,
  v2AdminListOutreachReplyClassificationOptions,
  v2AdminRerunOutreachReplyClassificationMutation,
  v2AdminRerunPortfolioOutreachReplyClassificationMutation,
} from '@workspace/api-backend/react-query'
import { Badge } from '@workspace/ui/components/badge'
import { Button } from '@workspace/ui/components/button'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellText, DataTable } from '@workspace/ui/components/data-table'
import { Pagination } from '@workspace/ui/components/pagination'
import { useState } from 'react'

type SearchParams = {
  page?: number
  status?: string
  messageId?: string
}

export const Route = createFileRoute('/admin/outreach/reply-classification')({
  component: AdminOutreachReplyClassificationPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    status: (search.status as string) || undefined,
    messageId: (search.messageId as string) || undefined,
  }),
})

type Item = V2AdminListOutreachReplyClassificationResponses[200]['data']['items'][number]

const LIMIT = 50
const STATUSES = ['PENDING', 'ENQUEUED', 'ACTIVE', 'COMPLETED', 'FAILED', 'CANCELED'] as const
type Status = (typeof STATUSES)[number]

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'secondary' | 'destructive'> = {
  PENDING: 'secondary',
  ENQUEUED: 'warning',
  ACTIVE: 'warning',
  COMPLETED: 'success',
  FAILED: 'destructive',
  CANCELED: 'secondary',
}

function formatDate(date: string | Date): string {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Multi-label class vocabulary, display order. Always shown (even at 0);
// unknown labels coming from the backend are appended dynamically.
const INVESTOR_LABEL_ORDER: string[] = [
  'interested',
  'call_proposal',
  'asked_question',
  'warm_intro',
  'referral',
  'redirect',
  'advisor',
  'not_an_investor',
  'not_a_fit',
  'already_invested',
  'out_of_mandate',
  'sector_scope_mismatch',
  'acknowledgement',
  'unclassified',
]

const PORTFOLIO_LABEL_ORDER: string[] = [
  'intro_accepted',
  'intro_conditional',
  'call_proposal',
  'asked_question',
  'referral',
  'redirect',
  'rejected',
  'acknowledgement',
  'unclassified',
]

// Badge variant per multi-label class (both tracks share the palette:
// positive=success, routing=warning, negative=destructive, neutral=secondary).
const LABEL_VARIANT: Record<string, 'success' | 'warning' | 'destructive' | 'secondary'> = {
  interested: 'success',
  call_proposal: 'success',
  asked_question: 'success',
  warm_intro: 'success',
  intro_accepted: 'success',
  intro_conditional: 'warning',
  referral: 'warning',
  redirect: 'warning',
  advisor: 'secondary',
  acknowledgement: 'secondary',
  not_an_investor: 'destructive',
  not_a_fit: 'destructive',
  already_invested: 'destructive',
  out_of_mandate: 'destructive',
  sector_scope_mismatch: 'destructive',
  rejected: 'destructive',
}

function GlobalReplyStats() {
  const { data } = useQuery(v2AdminGetOutreachReplyStatsOptions())
  const [period, setPeriod] = useState<'7d' | '30d'>('7d')
  const [type, setType] = useState<'investor' | 'portfolio'>('investor')

  if (!data?.data) return null

  const isPortfolio = type === 'portfolio'
  const w = isPortfolio
    ? period === '7d'
      ? data.data.portfolioWindow7d
      : data.data.portfolioWindow30d
    : period === '7d'
      ? data.data.window7d
      : data.data.window30d

  // Multi-label distribution from the backend (legacy rows are mapped onto the
  // same label vocabulary server-side, so one distribution covers the window).
  const counts: Record<string, number> = w.byLabel
  const baseKeys = isPortfolio ? PORTFOLIO_LABEL_ORDER : INVESTOR_LABEL_ORDER
  const extraKeys = Object.keys(counts).filter((k) => !baseKeys.includes(k))
  const categories = [...baseKeys, ...extraKeys]
    .filter((key) => (counts[key] ?? 0) > 0 || baseKeys.includes(key))
    .map((key) => ({ key, label: key.replaceAll('_', ' ') }))

  return (
    <div className="rounded-xl border border-border/60 bg-card/40 p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-baseline gap-4">
          <span className="text-2xl font-bold text-foreground">{w.totalReplies}</span>
          <span className="text-xs text-muted-foreground">total replies</span>
          {w.replyRatePct !== null && (
            <span className="text-xs text-muted-foreground">{w.replyRatePct}% reply rate</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-border/60 overflow-hidden text-xs">
            {(['investor', 'portfolio'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setType(t)}
                className={`px-3 py-1 capitalize ${type === t ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted/40'}`}
              >
                {t === 'investor' ? 'Investor' : 'Portfolio'}
              </button>
            ))}
          </div>
          <div className="flex rounded-lg border border-border/60 overflow-hidden text-xs">
            {(['7d', '30d'] as const).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPeriod(p)}
                className={`px-3 py-1 ${period === p ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted/40'}`}
              >
                {p === '7d' ? 'Last 7 days' : 'Last 30 days'}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(90px,1fr))] gap-2">
        {categories.map(({ key, label }) => (
          <div key={key} className="rounded-lg bg-muted/20 px-2 py-2 text-center">
            <div className="text-base font-semibold text-foreground">{counts[key] ?? 0}</div>
            <div className="text-[10px] leading-tight text-muted-foreground">{label}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

function AdminOutreachReplyClassificationPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const [selectedStatus, setSelectedStatus] = useState<Status | ''>((search.status as Status) ?? '')
  const currentPage = search.page ?? 1

  const { data: response, isLoading } = useQuery({
    ...v2AdminListOutreachReplyClassificationOptions({
      query: {
        page: currentPage,
        status: selectedStatus || undefined,
        messageId: search.messageId,
      },
    }),
    refetchInterval: (q) => {
      const data = q.state.data as V2AdminListOutreachReplyClassificationResponses[200] | undefined
      const items: Item[] = data?.data?.items ?? []
      const hasInflight = items.some(
        (it) => it.status === 'PENDING' || it.status === 'ENQUEUED' || it.status === 'ACTIVE'
      )
      return hasInflight ? 5000 : false
    },
  })

  const queryClient = useQueryClient()
  const invalidateList = () =>
    queryClient.invalidateQueries({
      queryKey: v2AdminListOutreachReplyClassificationOptions({
        query: {
          page: currentPage,
          status: selectedStatus || undefined,
          messageId: search.messageId,
        },
      }).queryKey,
    })
  const rerun = useMutation({
    ...v2AdminRerunOutreachReplyClassificationMutation(),
    onSuccess: invalidateList,
  })
  const rerunPortfolio = useMutation({
    ...v2AdminRerunPortfolioOutreachReplyClassificationMutation(),
    onSuccess: invalidateList,
  })

  const items: Item[] = response?.data?.items ?? []
  const total: number = response?.data?.total ?? 0
  const totalPages = Math.ceil(total / LIMIT)

  const columns: TableColumn<Item>[] = [
    {
      header: 'Run ID',
      width: '80px',
      render: (item) => <span className="font-mono text-xs">{item.id.slice(0, 8)}</span>,
    },
    {
      header: 'Startup',
      width: '80px',
      render: (item) =>
        item.startupId ? (
          <a
            href={`/admin/startups/${item.startupId}`}
            className="text-xs text-primary hover:underline"
            title={item.startupId}
          >
            {item.startupName ?? item.startupId.slice(0, 8)}
          </a>
        ) : (
          <CellText>—</CellText>
        ),
    },
    {
      header: 'Type',
      width: '100px',
      render: (item) => (
        <Badge variant={item.type === 'portfolio' ? 'secondary' : 'warning'} size="sm">
          {item.type === 'portfolio' ? 'Portfolio' : 'Investor'}
        </Badge>
      ),
    },
    {
      header: 'Message',
      width: '140px',
      render: (item) => (
        <div className="flex flex-col leading-tight">
          <span className="font-mono text-xs">
            {item.messageId ? item.messageId.slice(0, 8) : '—'}
          </span>
          <span className="text-xs text-foreground">{item.contactName ?? '—'}</span>
          <span className="text-[11px] text-muted-foreground">{item.investorName ?? '—'}</span>
        </div>
      ),
    },
    {
      header: 'Status',
      width: '110px',
      render: (item) => (
        <Badge variant={STATUS_VARIANT[item.status] ?? 'secondary'} size="sm">
          {item.status}
        </Badge>
      ),
    },
    {
      header: 'Result',
      render: (item) => {
        // Multi-label classifier output (new rows): show the full label set +
        // extracted metadata. Legacy rows (empty labels) fall through below.
        if (item.labels.length > 0) {
          const referralNames = (item.referral?.names ?? [])
            .map((n) => (n.firm ? `${n.name} (${n.firm})` : n.name))
            .join(', ')
          const redirectTarget = [item.redirect?.url, item.redirect?.email]
            .filter(Boolean)
            .join(' · ')
          const callTarget = [item.callProposal?.calendarLink, item.callProposal?.proposedTime]
            .filter(Boolean)
            .join(' · ')
          const details = [
            referralNames ? `→ ${referralNames}` : null,
            item.referral?.contactWillIntro ? 'will intro themselves' : null,
            item.referral?.detail,
            redirectTarget,
            callTarget,
            item.questionText,
            item.rejectionDetail,
          ].filter(Boolean)
          return (
            <CellText>
              <span className="flex flex-wrap items-center gap-1">
                {item.labels.map((label) => (
                  <Badge key={label} variant={LABEL_VARIANT[label] ?? 'secondary'} size="sm">
                    {label.replaceAll('_', ' ')}
                  </Badge>
                ))}
                {details.map((d, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: static per-render list; values can legitimately collide
                  <span key={i} className="text-xs text-muted-foreground">
                    {d}
                  </span>
                ))}
              </span>
            </CellText>
          )
        }
        if (item.rejectionReason) {
          return (
            <CellText>
              <Badge variant="destructive" size="sm">
                {item.rejectionReason}
              </Badge>
              {item.rejectionDetail ? (
                <span className="ml-2 text-xs text-muted-foreground">{item.rejectionDetail}</span>
              ) : null}
            </CellText>
          )
        }
        if (item.websiteRedirect) {
          return (
            <CellText>
              <Badge variant="warning" size="sm">
                website redirect
              </Badge>
              {item.websiteRedirectUrl ? (
                <span className="ml-2 truncate text-xs text-muted-foreground">
                  {item.websiteRedirectUrl}
                </span>
              ) : null}
            </CellText>
          )
        }
        if (item.warmIntro) {
          return (
            <Badge variant="success" size="sm">
              warm intro
            </Badge>
          )
        }
        if (item.warmIntroConditional) {
          return (
            <Badge variant="warning" size="sm">
              intro conditional
            </Badge>
          )
        }
        if (item.askedQuestion) {
          return (
            <CellText>
              <Badge variant="secondary" size="sm">
                asked question
              </Badge>
              {item.questionText ? (
                <span className="ml-2 text-xs text-muted-foreground">{item.questionText}</span>
              ) : null}
            </CellText>
          )
        }
        if (item.callScheduledCandidate) {
          return (
            <Badge variant="success" size="sm">
              call candidate
            </Badge>
          )
        }
        if (item.exhausted) {
          return (
            <Badge variant="destructive" size="sm">
              declined
            </Badge>
          )
        }
        // Classification finished with no specific signal → unclassified (legacy
        // rows; re-run to get multi-label chips).
        // Still in-flight (or failed) jobs have no result yet, so only badge once COMPLETED.
        if (item.status === 'COMPLETED') {
          return (
            <CellText>
              <Badge variant="secondary" size="sm">
                unclassified
              </Badge>
              {item.replySummary ? (
                <span className="ml-2 text-xs text-muted-foreground">{item.replySummary}</span>
              ) : null}
            </CellText>
          )
        }
        return (
          <CellText className="text-xs text-muted-foreground">{item.replySummary ?? '—'}</CellText>
        )
      },
    },
    {
      header: 'Summary / Error',
      render: (item) =>
        item.error ? (
          <span className="block max-w-md whitespace-normal break-words text-xs text-destructive">
            {item.error}
          </span>
        ) : item.replySummary ? (
          <span className="block max-w-md whitespace-normal break-words text-xs text-muted-foreground">
            {item.replySummary}
          </span>
        ) : (
          <CellText>—</CellText>
        ),
    },
    {
      header: 'Created',
      width: '140px',
      render: (item) => <CellText>{formatDate(item.createdAt)}</CellText>,
    },
    {
      header: 'Updated',
      width: '140px',
      render: (item) => <CellText>{formatDate(item.updatedAt)}</CellText>,
    },
    {
      header: '',
      width: '90px',
      render: (item) => {
        const inflight =
          item.status === 'PENDING' || item.status === 'ENQUEUED' || item.status === 'ACTIVE'
        const isPortfolio = item.type === 'portfolio'
        const isRerunning = isPortfolio
          ? rerunPortfolio.isPending &&
            rerunPortfolio.variables?.path?.portfolioMessageId === item.messageId
          : rerun.isPending && rerun.variables?.path?.messageId === item.messageId
        return (
          <Button
            size="sm"
            variant="secondary"
            disabled={inflight || isRerunning || !item.messageId}
            onClick={() =>
              isPortfolio
                ? rerunPortfolio.mutate({ path: { portfolioMessageId: item.messageId ?? '' } })
                : rerun.mutate({ path: { messageId: item.messageId ?? '' } })
            }
          >
            {isRerunning ? '…' : 'Retry'}
          </Button>
        )
      },
    },
  ]

  return (
    <div className="w-full space-y-6">
      <GlobalReplyStats />
      <div className="flex gap-2 flex-wrap">
        {(['', ...STATUSES] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => {
              setSelectedStatus(s)
              void navigate({
                search: { page: 1, status: s || undefined, messageId: search.messageId },
              })
            }}
            className={`rounded px-3 py-1 text-sm ${
              selectedStatus === s
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {s || 'All'}
          </button>
        ))}
      </div>

      <DataTable
        columns={columns}
        data={items}
        isLoading={isLoading}
        emptyMessage="No reply classification jobs found."
        keyExtractor={(item) => item.id}
      />

      <Pagination
        page={currentPage}
        totalPages={totalPages}
        onPageChange={(p) =>
          void navigate({
            search: {
              page: p,
              status: selectedStatus || undefined,
              messageId: search.messageId,
            },
          })
        }
      />
    </div>
  )
}
